"use client";

import { IconCheckOutline18, IconPencilOutline18, IconXmarkOutline18 } from "nucleo-ui-outline-18";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { useTemplatesStore, type Template } from "@/stores/templatesStore";
interface EditableTemplateTitleProps {
  template: Template;
  canEdit: boolean;
}
export function EditableTemplateTitle({
  template,
  canEdit
}: EditableTemplateTitleProps) {
  const updateTemplate = useTemplatesStore(state => state.updateTemplate);
  const [isEditing, setIsEditing] = useState(false);
  const [localName, setLocalName] = useState(template.name);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!isEditing) setLocalName(template.name);
  }, [template.name, isEditing]);
  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);
  const handleSave = useCallback(async () => {
    const trimmed = localName.trim();
    if (!trimmed) {
      setError("Name is required");
      return;
    }
    if (trimmed === template.name) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await updateTemplate(template.id, {
        name: trimmed
      });
      setIsEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setIsSaving(false);
    }
  }, [localName, template.id, template.name, updateTemplate]);
  const handleCancel = useCallback(() => {
    setLocalName(template.name);
    setError(null);
    setIsEditing(false);
  }, [template.name]);
  if (!isEditing) {
    return <div className="flex items-center gap-2">
        <h1 className="text-content-secondary leading-label text-2xl font-medium">{template.name}</h1>
        {canEdit && <Button variant="ghost" size="xs" iconOnly onClick={() => setIsEditing(true)} title="Edit name" aria-label="Edit name">
            <IconPencilOutline18 className="h-4 w-4" />
          </Button>}
      </div>;
  }
  return <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Input ref={inputRef} type="text" value={localName} maxLength={255} onChange={e_0 => setLocalName(e_0.target.value)} onKeyDown={e_1 => {
        if (e_1.key === "Enter") {
          e_1.preventDefault();
          void handleSave();
        } else if (e_1.key === "Escape") {
          handleCancel();
        }
      }} className="border-ds-neutral-250 text-content-secondary focus:border-ds-focus-brand focus:ring-ds-focus-brand h-auto rounded-lg py-1 text-2xl font-medium placeholder:text-[#a3a3a3] focus:ring-1" placeholder="Template name..." />
        <Button variant="brand" size="sm" iconOnly onClick={() => void handleSave()} disabled={isSaving} title="Save" aria-label="Save">
          {isSaving ? <PixelLoader size="xs" /> : <IconCheckOutline18 className="h-4 w-4" />}
        </Button>
        <Button variant="white" size="sm" iconOnly onClick={handleCancel} disabled={isSaving} title="Cancel" aria-label="Cancel">
          <IconXmarkOutline18 className="h-4 w-4" />
        </Button>
      </div>
      {error && <p className="text-2-5xs text-red-600">{error}</p>}
    </div>;
}